import { find, startCase, toUpper } from "lodash/fp";

import ROUTES from "@app/common/routes";
import type {
  IntegrationScope,
  UserDistributionSetting,
  UserSettings,
} from "@app/common/types";
import { IntegrationProviderCategory } from "@app/common/types/providerTypes";

import { SelectedTabConfig } from "./types";
import UserIntegrations from "./UserIntegrations";
import WorkspaceIntegrations from "./WorkspaceIntegrations";

export const shouldShowParticipantDisclaimer = (description: string) =>
  description.includes("participants");

export const getCategoryLabel = (
  category: IntegrationProviderCategory
): string => {
  switch (category) {
    case "crm":
      return toUpper(category);
    default:
      return startCase(category);
  }
};

const getRecipientsFromUserDistributionSettings = ({
  userSettings,
  provider,
  category,
}: {
  userSettings: UserSettings;
  provider: UserDistributionSetting["method"];
  category: UserDistributionSetting["category"];
}) => {
  const settingsForProvider = find(
    { method: provider, category },
    userSettings.userDistributionSettings
  );

  if (!settingsForProvider || !settingsForProvider.isEnabled) {
    return "no one";
  }

  return settingsForProvider.sendTo === "me"
    ? "only me"
    : "me and participants* with access";
};

export const getMeetingRecapRecipientsDescriptionFromUserSettings = ({
  userSettings,
  provider,
}: {
  userSettings: UserSettings;
  provider: UserDistributionSetting["method"];
}): string => {
  return getRecipientsFromUserDistributionSettings({
    userSettings,
    provider,
    category: "meeting_report",
  });
};

export const getMeetingPreReadRecipientsDescriptionFromUserSettings = ({
  userSettings,
  provider,
}: {
  userSettings: UserSettings;
  provider: UserDistributionSetting["method"];
}): string => {
  return getRecipientsFromUserDistributionSettings({
    userSettings,
    provider,
    category: "meeting_pre_read",
  });
};

export const getTopicReportRecipientsDescriptionFromUserSettings = ({
  userSettings,
  provider,
}: {
  userSettings: UserSettings;
  provider: UserDistributionSetting["method"];
}): string => {
  return getRecipientsFromUserDistributionSettings({
    userSettings,
    provider,
    category: "topic_summary",
  });
};

export const getTabs = (isMobile: boolean): SelectedTabConfig[] => [
  {
    label: "Your Integrations",
    value: "user",
    path: isMobile
      ? ROUTES.INTEGRATIONS_USER_ROOT
      : ROUTES.INTEGRATIONS_GOOGLE_CALENDAR,
    content: UserIntegrations,
  },
  {
    label: "Workspace",
    value: "workspace",
    path: isMobile
      ? ROUTES.INTEGRATIONS_WORKSPACE_ROOT
      : ROUTES.INTEGRATIONS_WORKSPACE_SLACK,
    content: WorkspaceIntegrations,
  },
];

export const getSelectedTab = (
  scope: string | undefined,
  tabs: SelectedTabConfig[]
) => {
  const selectedTab = (scope ?? "user") as IntegrationScope;
  const selectedTabConfig = tabs.length
    ? find({ value: selectedTab }, tabs) ?? tabs[0]
    : null;

  return {
    selectedTab,
    selectedTabConfig,
  };
};
